import { useCallback, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";

/* ---------- helpers ---------- */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/;

function isLikelyDate(v) {
  if (v == null) return false;
  if (v instanceof Date) return true;
  if (typeof v === "number") return v > 1e9 && v < 1e13; // ms epoch (or sec * 1000)
  if (typeof v === "string") return ISO_DATE_RE.test(v);
  return false;
}

function toMs(v) {
  if (v == null) return null;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v; // sec → ms heuristic
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Format a date as HH:MM (always — regardless of how long the range is). */
function formatHourMinute(value) {
  const ms = toMs(value);
  if (ms == null) return String(value ?? "");
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Fallback for non-date X values: keep them short. */
function shortenNonDate(v) {
  if (v == null) return "";
  if (typeof v === "string") {
    return v.length > 14 ? v.slice(0, 12) + "…" : v;
  }
  return v;
}

function fmtNumber(v) {
  if (v == null) return "";
  if (typeof v !== "number") return v;
  if (Math.abs(v) >= 1000000) return (v / 1000000).toFixed(1) + "M";
  if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + "k";
  if (Math.abs(v) < 1 && v !== 0) return v.toFixed(2);
  return Math.round(v * 100) / 100;
}

/* ---------- component ---------- */

// Static styling primitives — hoisted to avoid recreating objects on every render.
const TICK_STYLE = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };
const AXIS_LINE_STYLE = { stroke: "hsl(var(--border))" };
const TOOLTIP_STYLE = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
  color: "hsl(var(--popover-foreground))",
};
const TOOLTIP_CURSOR = { fill: "hsl(var(--accent))", opacity: 0.4 };
const CHART_MARGIN = { top: 8, right: 16, left: 0, bottom: 8 };
const ACTIVE_DOT = { r: 4 };
const BAR_RADIUS = [4, 4, 0, 0];

export default function ChartRenderer({
  type = "line",
  rows = [],
  xField = "x",
  yField = "y",
  xLabel = "",
  yLabel = "",
  color = "#3B82F6",
  height = 280,
  thresholds = [],
  /** Optional [fromIso, toIso] — used to sync X-axis across charts on the page. */
  xRange = null,
  /** Y axis domain settings */
  yMode = "auto", // 'auto' | 'fixed'
  yMin = null,
  yMax = null,
}) {
  // Detect if X axis is time-based: look at first defined sample
  const xIsTime = useMemo(() => {
    if (xRange?.from || xRange?.to) return true;
    const sample = (rows || []).find((r) => r?.[xField] != null);
    return sample ? isLikelyDate(sample[xField]) : false;
  }, [rows, xField, xRange]);

  // Coerce data:
  //  - if X is time, convert xField to ms (numeric) so we can use a continuous axis
  //  - Y always coerced to number when possible
  const data = useMemo(() => {
    return (rows || []).map((r) => {
      const yRaw = r[yField];
      const yNum = typeof yRaw === "string" ? Number(yRaw) : yRaw;
      const xRaw = r[xField];
      const xVal = xIsTime ? toMs(xRaw) : xRaw;
      return {
        ...r,
        [xField]: xVal,
        [yField]: Number.isFinite(yNum) ? yNum : yRaw,
      };
    });
  }, [rows, xField, yField, xIsTime]);

  // X-axis configuration
  const xAxisProps = useMemo(() => {
    const base = {
      dataKey: xField,
      tick: TICK_STYLE,
      axisLine: AXIS_LINE_STYLE,
      tickLine: false,
      label: xLabel
        ? {
            value: xLabel,
            position: "insideBottom",
            offset: -2,
            style: { fill: "hsl(var(--muted-foreground))", fontSize: 11 },
          }
        : undefined,
    };
    if (xIsTime) {
      const fromMs = xRange?.from ? toMs(xRange.from) : "dataMin";
      const toMsVal = xRange?.to ? toMs(xRange.to) : "dataMax";
      return {
        ...base,
        type: "number",
        scale: "time",
        domain: [fromMs ?? "dataMin", toMsVal ?? "dataMax"],
        allowDataOverflow: true,
        tickFormatter: formatHourMinute,
      };
    }
    return { ...base, tickFormatter: shortenNonDate };
  }, [xField, xIsTime, xLabel, xRange]);

  // Y-axis domain — memoized to avoid re-creating array on every render
  const yDomain = useMemo(() => {
    if (yMode === "fixed") {
      const lo = Number.isFinite(yMin) ? yMin : "dataMin";
      const hi = Number.isFinite(yMax) ? yMax : "dataMax";
      return [lo, hi];
    }
    return ["auto", "auto"];
  }, [yMode, yMin, yMax]);

  const yLabelProp = useMemo(
    () =>
      yLabel
        ? {
            value: yLabel,
            angle: -90,
            position: "insideLeft",
            style: { fill: "hsl(var(--muted-foreground))", fontSize: 11, textAnchor: "middle" },
          }
        : undefined,
    [yLabel]
  );

  const tooltipFormatter = useCallback(
    (value) => [fmtNumber(value), yField],
    [yField]
  );

  const tooltipLabelFormatter = useCallback(
    (label) => {
      if (xIsTime) {
        const ms = toMs(label);
        if (ms == null) return String(label ?? "");
        const d = new Date(ms);
        return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
      }
      return String(label ?? "");
    },
    [xIsTime]
  );

  const common = (
    <>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis {...xAxisProps} />
      <YAxis
        tickFormatter={fmtNumber}
        tick={TICK_STYLE}
        axisLine={AXIS_LINE_STYLE}
        tickLine={false}
        width={48}
        domain={yDomain}
        allowDataOverflow={yMode === "fixed"}
        label={yLabelProp}
      />
      <Tooltip
        contentStyle={TOOLTIP_STYLE}
        cursor={TOOLTIP_CURSOR}
        formatter={tooltipFormatter}
        labelFormatter={tooltipLabelFormatter}
      />
      {thresholds.map((t, i) => (
        <ReferenceLine
          key={`th-${t.value ?? "n"}-${i}`}
          y={t.value}
          stroke={t.color || "hsl(var(--destructive))"}
          strokeDasharray="4 4"
          strokeWidth={1.5}
          label={{
            value: t.label || `≥ ${t.value}`,
            position: "right",
            fill: t.color || "hsl(var(--destructive))",
            fontSize: 10,
          }}
        />
      ))}
    </>
  );

  return (
    <div style={{ width: "100%", height }} data-testid="chart-canvas">
      <ResponsiveContainer width="100%" height="100%">
        {type === "line" && (
          <LineChart data={data} margin={CHART_MARGIN}>
            {common}
            <Line
              type="monotone"
              dataKey={yField}
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={ACTIVE_DOT}
              isAnimationActive={false}
            />
          </LineChart>
        )}
        {type === "area" && (
          <AreaChart data={data} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id={`grad-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.45} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            {common}
            <Area
              type="monotone"
              dataKey={yField}
              stroke={color}
              strokeWidth={2}
              fill={`url(#grad-${color.replace("#", "")})`}
              isAnimationActive={false}
            />
          </AreaChart>
        )}
        {type === "bar" && (
          <BarChart data={data} margin={CHART_MARGIN}>
            {common}
            <Bar dataKey={yField} fill={color} radius={BAR_RADIUS} isAnimationActive={false}>
              {data.map((row, i) => (
                <Cell key={`bar-cell-${row?.[xField] ?? i}`} fill={color} />
              ))}
            </Bar>
          </BarChart>
        )}
        {type === "scatter" && (
          <ScatterChart margin={CHART_MARGIN}>
            {common}
            <Scatter data={data} dataKey={yField} fill={color} />
          </ScatterChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
